/**
 * Utility functions for formatting data
 */

/**
 * Formats a currency value
 */
export function formatCurrency(
    value: number | string,
    options: Intl.NumberFormatOptions = {},
): string {
    const numValue = typeof value === 'string' ? Number(formatAmount(value)) : value;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        ...options,
    }).format(numValue);
}

/**
 * Masks a Social Security Number
 */
export function maskSsn(ssn: string): string {
    return `XXX-XX-${ssn.slice(-4)}`;
}
export const hideSsn = maskSsn;

const DATE_ONLY_ISO = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Formats a date as MM/DD/YYYY.
 * Date-only ISO strings (YYYY-MM-DD) are treated as calendar dates, not UTC timestamps,
 * so they display the same month/day/year in every timezone. Date-times keep local-time formatting.
 */
export function formatDate(date: string): string {
    const options: Intl.DateTimeFormatOptions = {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        ...(DATE_ONLY_ISO.test(date) ? { timeZone: 'UTC' } : {}),
    };
    return new Date(date).toLocaleDateString('en-US', options);
}

/**
 * Removes all non-digit characters from a string
 */
export function digitsOnly(value: string): string {
    return value.replace(/\D/g, '');
}

/**
 * Formats a Social Security Number with dashes as user types
 */
export function formatSsn(ssn: string): string {
    const result = ssn.replace(/[^(\d|x)]/gi, '');
    if (result.length <= 3) {
        return result.replace(/(.{1,3})/, '$1');
    }
    if (result.length <= 5) {
        return result.replace(/(.{1,3})(.{1,2})/, '$1-$2');
    }
    return result.replace(/(.{1,3})(.{1,2})(.{1,4})/, '$1-$2-$3');
}

/**
 * Formats phone number with progressive formatting as user types
 */
export function formatPhoneNumber(phoneNumber: string): string {
    const result = phoneNumber.replace(/[^\d]/gi, '');
    if (result.length <= 3) {
        return result.replace(/(.{1,3})/, '$1');
    }
    if (result.length <= 6) {
        return result.replace(/(.{1,3})(.{1,3})/, '($1) $2');
    }
    return result.replace(/(.{1,3})(.{1,3})(.{1,4})/, '($1) $2-$3');
}

/**
 * Progressive phone formatter while typing: displays (123) 456-7890 format
 */
export function formatPhoneProgressive(value: string): string {
    const digits = digitsOnly(value).slice(0, 10);
    if (digits.length <= 3) {
        return digits;
    }
    if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Formats last 4 digits of SSN for input fields
 */
export function formatSsnLast4(value: string): string {
    return digitsOnly(value).slice(0, 4);
}

/**
 * Formats amount string by removing non-numeric characters except decimal
 */
export function formatAmount(amount: string): string {
    let result = amount.replace(/[^\d.]/gi, '');
    result = result.replace(/^0+(\d)/g, '$1');
    return result;
}

/**
 * Formats amount to exactly 2 decimal places
 */
export function formatTwoDigitsAmount(amount: string): string {
    let value: string = amount.replace(/[^\d.]/gi, '');
    if (value.length === 0) {
        return value;
    }
    value = value.replace(/^0+/g, '');
    if (value.length === 0) {
        return '0';
    }
    if (value.startsWith('.')) {
        value = `0${value}`;
    }
    const numbers = value.split('.');
    if (numbers.length > 1) {
        return `${numbers[0]}.${numbers[1].slice(0, 2)}`;
    }
    return value;
}

/**
 * Validates amount format (allows decimal with up to 2 digits)
 */
export function validateAmount(amount: string): boolean {
    const regexp = /^\d*.?\d{0,2}$/;
    return regexp.test(amount);
}

/**
 * Formats spouse contact info (handles phone numbers or other formats)
 */
export function formatSpouseContact(contactInfo: string): string {
    const result = contactInfo.replace(/[\(-\) ]/gi, '');
    if (result.length <= 9 && /^\d{1,9}$/i.test(result)) {
        return formatPhoneNumber(contactInfo);
    }
    return contactInfo;
}

/**
 * Formats date with specific locale options
 */
export function formatDateWithLocale(date: Date): string {
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(date);
}

/**
 * Formats date in long format (MON DD YYYY)
 */
export function formatLongDate(date: string | undefined): string {
    if (!date) {
        return '';
    }
    const dateTime = new Date(date);
    const monthNames = [
        'JAN',
        'FEB',
        'MAR',
        'APR',
        'MAY',
        'JUN',
        'JUL',
        'AUG',
        'SEP',
        'OCT',
        'NOV',
        'DEC',
    ];
    const monthNumber = dateTime.getUTCMonth();
    const month = monthNames[monthNumber];
    const dateOfMonth = dateTime.getUTCDate();
    const year = dateTime.getUTCFullYear();

    return `${month}\xa0${dateOfMonth}\n${year}`;
}

/**
 * Formats date in locale format
 */
export function formatLocaleDate(date: string | undefined): string {
    if (!date) {
        return '';
    }
    const utcOptions: Intl.DateTimeFormatOptions = { timeZone: 'UTC' };
    return new Date(date).toLocaleDateString(undefined, utcOptions);
}

/**
 * Removes all non-digit characters (alias for consistency)
 */
export function toCleanNumber(value: string): string {
    return value.replace(/[^\d]/gi, '');
}

/**
 * Gets billing frequency display string
 */
export function getBillingFrequencyString(freq: number | undefined): string {
    switch (freq) {
        case 0:
            return 'One Time';
        case 1:
            return 'Monthly';
        case 2:
            return 'Every other month';
        case 3:
            return 'Quarterly';
        case 6:
            return 'Bi-Annual';
        case 12:
            return 'Annual';
        default:
            return 'Unknown';
    }
}

/**
 * Gets duration with billing frequency display string
 */
export function getDurationBillingString(duration?: number, billingFrequency?: number): string {
    let result = '';
    if (duration === 1) {
        result = '1 month';
    } else if (duration) {
        result = `${duration} months`;
    } else {
        result = 'Ongoing';
    }
    return `${result} - ${getBillingFrequencyString(billingFrequency)}`;
}

/**
 * Money formatter instance for consistent currency formatting
 */
export const moneyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
});

// Legacy aliases for backward compatibility
export const currency = formatCurrency;
export const twoDigitsAmount = formatTwoDigitsAmount;
export const amountValidator = validateAmount;
export const spouseContactFormatter = formatSpouseContact;
export const dateFormatter = formatDateWithLocale;
export const toNumber = toCleanNumber;
export const longDate = formatLongDate;
export const localeDate = formatLocaleDate;
